import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2,
  ArrowLeft,
  LogOut,
  Menu,
  UserCircle2,
  FileSignature,
  Stethoscope,
  IdCard,
  ClipboardList,
  CalendarDays,
  Ambulance,
  BarChart3,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";

import AdminLogin from "@/pages/AdminLogin";
import AdminSidebar, { NAV_GROUPS } from "@/components/AdminSidebar";
import { useAiContext } from "@/lib/aiContext";
import DashboardHome from "@/components/DashboardHome";
import PlaceholderPanel from "@/components/PlaceholderPanel";
import ConsumerLeadsPanel from "@/components/ConsumerLeadsPanel";
import FacilityLeadsPanel from "@/components/FacilityLeadsPanel";
import DocumentsPanel from "@/components/DocumentsPanel";
import OperationsDocsPanel from "@/components/documents/DocumentsWorkspace";
import ContractsPanel from "@/components/ContractsPanel";
import ServicesPanel from "@/components/ServicesPanel";
import TasksPanel from "@/components/TasksPanel";
import ClientsPanel from "@/components/ClientsPanel";
import StaffPanel from "@/components/employees/EmployeeComplianceCenter";
import DispatchPanel from "@/components/DispatchPanel";
import CalendarPanel from "@/components/CalendarPanel";
import ReportsPanel from "@/components/ReportsPanel";
import HelpCenterPanel from "@/components/HelpCenterPanel";
import ChatPanel from "@/components/ChatPanel";
import SettingsPanel from "@/components/SettingsPanel";
import RefundCenterPanel from "@/components/RefundCenterPanel";
import FloatingAssistantButton from "@/components/FloatingAssistantButton";
import { getAdminToken, clearAdminToken, adminMe, getAdminUser, getChatUnread } from "@/lib/api";

const PLACEHOLDERS = {
  clients: {
    title: "Clients",
    description: "A unified directory of every patient, family, and facility you serve.",
    icon: UserCircle2,
    features: [
      "Searchable client directory",
      "Service history & timeline",
      "Contact preferences & quiet hours",
      "Linked contracts & invoices",
      "Notes, attachments & reminders",
      "Bulk tagging and segments",
    ],
  },
  contracts: {
    title: "Contracts",
    description: "Track every active, pending, and expired contract with renewal alerts.",
    icon: FileSignature,
    features: [
      "Upload signed PDFs (GridFS)",
      "Status: Draft · Pending · Active · Expired",
      "Renewal date reminders (30/60/90)",
      "Link to client or facility",
      "Internal notes & change history",
      "Quick search and filter",
    ],
  },
  services: {
    title: "Services",
    description: "Toggle services Available/Unavailable — syncs instantly with the public site.",
    icon: Stethoscope,
    features: [
      "Toggle availability per service",
      "Hide unavailable services on /request",
      "Edit display name & description",
      "Set base pricing (optional)",
      "Reorder card priority",
      "Service-level analytics",
    ],
  },
  staff: {
    title: "Employees",
    description: "Full compliance dashboard — profiles, credentials, onboarding, and dispatch readiness.",
    icon: IdCard,
    features: [
      "Employee profiles with photo",
      "Credential cards with expiry tracking",
      "Onboarding checklist auto-gate",
      "Dispatch readiness enforcement",
      "Activity timeline",
      "Compliance dashboard",
    ],
  },
  tasks: {
    title: "Tasks & Notes",
    description: "Personal reminders, follow-ups, and team todo items for the entire operation.",
    icon: ClipboardList,
    features: [
      "Create tasks with due dates",
      "Assign to Beckett or Jazmin",
      "Link to a lead, client, or facility",
      "Daily digest of overdue items",
      "Quick-add from any record",
      "Filter by status & owner",
    ],
  },
  calendar: {
    title: "Calendar",
    description: "Visualize every scheduled service, follow-up, and renewal in one calendar.",
    icon: CalendarDays,
    features: [
      "Month / week / day views",
      "Color-coded by service type",
      "Renewal & follow-up overlays",
      "Drag-and-drop reschedule",
      "Subscribe via iCal feed",
      "Sync with Google Calendar (planned)",
    ],
  },
  dispatch: {
    title: "Dispatch Center",
    description: "Real-time service dispatch workflow from request to completion.",
    icon: Ambulance,
    features: [
      "Pending → Assigned → En Route → On Site → Completed",
      "Assign field staff to a request",
      "Live status board",
      "SMS notification to dispatcher (Twilio)",
      "ETA & arrival tracking",
      "Completion notes & signatures",
    ],
  },
  reports: {
    title: "Reports & Analytics",
    description: "Operational insights — revenue, conversion, service mix, and team performance.",
    icon: BarChart3,
    features: [
      "Revenue trends by month",
      "Lead → contract conversion funnel",
      "Service mix breakdown",
      "Top facilities by volume",
      "Staff utilization metrics",
      "CSV export of any report",
    ],
  },
};

const NAV_LABEL_BY_ID = (() => {
  const m = {};
  NAV_GROUPS.forEach((g) => g.items.forEach((i) => { m[i.id] = i.label; }));
  return m;
})();

export default function Admin() {
  const [authed, setAuthed] = useState(() => !!getAdminToken());
  const [authChecking, setAuthChecking] = useState(() => !!getAdminToken());
  const [user, setUser] = useState(() => getAdminUser());
  const [active, setActive] = useState(() => {
    // Honor ?tab= query so 'Back to Leads' from a lead workspace returns to the correct panel.
    if (typeof window !== "undefined") {
      const t = new URLSearchParams(window.location.search).get("tab");
      if (t) return t;
    }
    return "dashboard";
  });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);

  // Surface the active page to the floating AI Assistant so the AI can
  // answer / propose actions scoped to whatever the admin is looking at.
  const aiCtx = useAiContext();
  useEffect(() => {
    const map = {
      dashboard: { label: "Dashboard" },
      consumer: { label: "Consumer leads", page: "leads" },
      facility: { label: "Facilities & providers", page: "facilities" },
      clients: { label: "Clients directory", page: "clients" },
      dispatch: { label: "Dispatch Center", page: "dispatch" },
      calendar: { label: "Calendar" },
      reports: { label: "Reports & billing", page: "billing" },
      tasks: { label: "Tasks" },
      contracts: { label: "Contracts", page: "billing" },
      staff: { label: "Staff" },
      settings: { label: "Settings" },
    };
    const meta = map[active] || { label: active };
    aiCtx.set({ page: meta.page || active, entity_type: null, entity_id: null, label: meta.label });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // Hide /admin from search engines
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow, noarchive, nosnippet";
    document.head.appendChild(meta);
    const prevTitle = document.title;
    document.title = "Admin · T&J Clinical Solutions";
    return () => {
      document.head.removeChild(meta);
      document.title = prevTitle;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const verify = async () => {
      if (!getAdminToken()) {
        setAuthed(false);
        setAuthChecking(false);
        return;
      }
      try {
        await adminMe();
        if (!cancelled) setAuthed(true);
      } catch {
        if (!cancelled) {
          clearAdminToken();
          setAuthed(false);
        }
      } finally {
        if (!cancelled) setAuthChecking(false);
      }
    };
    verify();
    return () => { cancelled = true; };
  }, []);

  // Poll team-chat unread count when authed; pause while viewing chat.
  useEffect(() => {
    if (!authed) return undefined;
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const data = await getChatUnread();
        if (!cancelled) setChatUnread(active === "chat" ? 0 : (data.unread || 0));
      } catch { /* non-fatal */ }
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 10000);
    return () => { cancelled = true; clearInterval(t); };
  }, [authed, active]);

  const onLogout = () => {
    clearAdminToken();
    setAuthed(false);
    setUser(null);
    toast.success("Signed out.");
  };

  const onLoggedIn = () => {
    setAuthed(true);
    setUser(getAdminUser());
    // Preserve a ?tab=... deep link across login so users land on the
    // intended panel (e.g. /admin?tab=facility) instead of always Dashboard.
    let initialTab = "dashboard";
    if (typeof window !== "undefined") {
      const t = new URLSearchParams(window.location.search).get("tab");
      if (t) initialTab = t;
    }
    setActive(initialTab);
  };

  const onSessionExpired = () => setAuthed(false);

  const handleSelect = (id) => {
    setActive(id);
    setMobileNavOpen(false);
    // Keep the URL in sync with the active tab so refreshes/back-button
    // and external links remain deep-linkable.
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.set("tab", id);
      // Drop transient sub-filters so each tab gets a clean URL when switched.
      if (id !== "consumer") url.searchParams.delete("status");
      window.history.replaceState({}, "", url.toString());
    }
  };

  if (authChecking) {
    return (
      <div className="min-h-screen grid place-items-center bg-white">
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: "#0B2545" }} />
      </div>
    );
  }

  if (!authed) {
    return <AdminLogin onLoggedIn={onLoggedIn} />;
  }

  const renderContent = () => {
    switch (active) {
      case "dashboard":
        return <DashboardHome onNavigate={handleSelect} onSessionExpired={onSessionExpired} user={user} chatUnread={chatUnread} />;
      case "consumer":
        return <ConsumerLeadsPanel onSessionExpired={onSessionExpired} onNavigate={handleSelect} />;
      case "facility":
        return <FacilityLeadsPanel onSessionExpired={onSessionExpired} />;
      case "documents":
        return <DocumentsPanel onSessionExpired={onSessionExpired} />;
      case "operations":
        return <OperationsDocsPanel onSessionExpired={onSessionExpired} />;
      case "contracts":
        return <ContractsPanel onSessionExpired={onSessionExpired} />;
      case "services":
        return <ServicesPanel onSessionExpired={onSessionExpired} />;
      case "tasks":
        return <TasksPanel onSessionExpired={onSessionExpired} />;
      case "clients":
        return <ClientsPanel onSessionExpired={onSessionExpired} />;
      case "staff":
        return <StaffPanel onSessionExpired={onSessionExpired} />;
      case "dispatch":
        return <DispatchPanel onSessionExpired={onSessionExpired} />;
      case "calendar":
        return <CalendarPanel onSessionExpired={onSessionExpired} />;
      case "reports":
        return <ReportsPanel onSessionExpired={onSessionExpired} />;
      case "help":
        return <HelpCenterPanel onNavigate={handleSelect} onSessionExpired={onSessionExpired} />;
      case "chat":
        return <ChatPanel onSessionExpired={onSessionExpired} />;
      case "settings":
        return <SettingsPanel onSessionExpired={onSessionExpired} />;
      case "refunds":
        return <RefundCenterPanel onSessionExpired={onSessionExpired} />;
      default: {
        const ph = PLACEHOLDERS[active];
        if (!ph) return null;
        return (
          <PlaceholderPanel
            title={ph.title}
            description={ph.description}
            icon={ph.icon}
            features={ph.features}
            onBack={() => handleSelect("dashboard")}
          />
        );
      }
    }
  };

  const activeLabel = NAV_LABEL_BY_ID[active] || "Dashboard";

  return (
    <div className="min-h-screen flex" style={{ background: "#F8FAFC" }}>
      <FloatingAssistantButton />
      {/* Desktop sidebar */}
      <div className="hidden lg:block sticky top-0 h-screen">
        <AdminSidebar active={active} onSelect={handleSelect} user={user} dynamicBadges={{ chatUnread }} />
      </div>

      {/* Mobile slide-over */}
      {mobileNavOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex" data-testid="mobile-nav-overlay">
          <div
            className="absolute inset-0"
            style={{ background: "rgba(11,37,69,0.55)" }}
            onClick={() => setMobileNavOpen(false)}
          />
          <div className="relative h-full">
            <AdminSidebar active={active} onSelect={handleSelect} user={user} dynamicBadges={{ chatUnread }} />
          </div>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <header
          className="sticky top-0 z-30 border-b bg-white/90 backdrop-blur"
          style={{ borderColor: "#E2E8F0" }}
        >
          <div className="px-4 sm:px-8 py-3.5 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMobileNavOpen(true)}
                className="lg:hidden h-9 w-9 p-0"
                style={{ color: "#0B2545" }}
                data-testid="open-mobile-nav"
              >
                <Menu className="h-5 w-5" />
              </Button>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.22em] font-medium" style={{ color: "#7A5E12" }}>
                  Admin Console
                </div>
                <div className="font-display text-base sm:text-lg font-semibold truncate" style={{ color: "#0B2545" }} data-testid="admin-active-title">
                  {activeLabel}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              {user ? (
                <div
                  className="hidden sm:flex items-center gap-2.5 rounded-full border px-3 py-1.5"
                  style={{ borderColor: "#E2E8F0", background: "#F8FAFC" }}
                  data-testid="admin-current-user"
                >
                  <span
                    className="grid place-items-center h-7 w-7 rounded-full font-display text-xs font-bold shrink-0"
                    style={{ background: "rgba(212,175,55,0.18)", color: "#7A5E12" }}
                  >
                    {user.name[0]}
                  </span>
                  <div className="leading-tight">
                    <div className="text-xs font-semibold" style={{ color: "#0B2545" }} data-testid="admin-welcome-name">
                      {user.name}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "#7A5E12" }} data-testid="admin-welcome-role">
                      {user.role}
                    </div>
                  </div>
                </div>
              ) : null}
              <Link
                to="/"
                className="hidden sm:inline-flex items-center gap-2 text-sm"
                style={{ color: "#0B2545" }}
                data-testid="back-to-form"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="hidden md:inline">Public site</span>
              </Link>
              <Button
                variant="outline"
                size="sm"
                onClick={onLogout}
                className="h-9 px-3 text-xs"
                style={{ borderColor: "#E2E8F0", color: "#0B2545" }}
                data-testid="admin-logout-button"
              >
                <LogOut className="h-3.5 w-3.5 mr-1.5" />
                Sign out
              </Button>
            </div>
          </div>
        </header>

        <main className="flex-1 px-4 sm:px-8 py-7" data-testid="admin-main-content">
          <div className="mx-auto max-w-[1400px]">{renderContent()}</div>
        </main>
      </div>
    </div>
  );
}
